/**
 * Reporting Dashboard — Team analytics view.
 *
 * Shows aggregate metrics, daily hours chart, timekeeper table,
 * and top clients/matters breakdown. Data is pre-aggregated by the backend.
 * Read-only — no editing. Click analytics surfaces to drill down to the
 * matching entries.
 */

import { Check, Download, Filter } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import {
	type Entry,
	fetchAllEntries,
	useReportsCache,
} from "@/shared/cache";
import { cn } from "@/shared/lib/utils";
import { getFirmDisplayName } from "@/shared/utils/firm";
import { logger } from "@/shared/utils/logger";
import { useApi } from "@/shared/utils/request";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { Button } from "@/v2/components/ui/button";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import {
	DATE_PRESET_LABELS,
	type DatePreset,
	getDateRange,
	type MemberRow,
	toDateKey,
	useReportingDashboard,
} from "../hooks/useReportingDashboard";
import { makeEntryPredicate } from "./Entries/entryFilter";
import { entryFiltersToEntriesScope } from "./Entries/entryQueryScope";
import { isDefaultFilters } from "./Entries/filters/defaults";
import {
	type FieldOptions,
	type FieldShortcut,
	FilterMenu,
} from "./Entries/filters/FilterMenu";
import { normalizeOptions } from "./Entries/filters/fields";
import type {
	EntryFilterState,
	EnumFilterField,
	NonStatusMultiSelectFilterField,
} from "./Entries/filters/types";
import {
	filterStateParser,
	serializeEntryFilters,
} from "./Entries/filters/urlState";
import { exportReportingDashboardToExcel } from "./exportReportingDashboard";

// Chart colors (brand palette)
const COLORS = {
	exported: "var(--chart-1)",
	tracked: "var(--chart-2)",
};

const CHART_COLORS = {
	grid: "var(--border)",
	axis: "var(--muted-foreground)",
	cursor: "color-mix(in srgb, var(--foreground) 6%, transparent)",
};

// ─────────────────────────────────────────────
// Stat Card
// ─────────────────────────────────────────────

function StatCard({
	label,
	value,
	unit,
	subtitle,
	gradient,
	onClick,
	ariaLabel,
}: {
	label: string;
	value: string;
	unit?: string;
	subtitle?: string;
	gradient: string;
	onClick?: () => void;
	ariaLabel?: string;
}) {
	const className = `relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${gradient} p-4 min-w-[130px] text-left ${onClick ? "cursor-pointer transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" : ""}`;
	const content = (
		<>
			<div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
			<p className="text-xs font-medium text-white/70 mb-1">{label}</p>
			<p className="text-2xl font-bold text-white tabular-nums">
				{value}
				{unit && (
					<span className="text-sm font-normal text-white/70 ml-1">{unit}</span>
				)}
			</p>
			{subtitle && <p className="text-xs text-white/50 mt-0.5">{subtitle}</p>}
		</>
	);

	if (onClick) {
		return (
			<button
				type="button"
				className={className}
				onClick={onClick}
				aria-label={ariaLabel}
			>
				{content}
			</button>
		);
	}

	return <div className={className}>{content}</div>;
}

// ─────────────────────────────────────────────
// Custom Tooltip
// ─────────────────────────────────────────────

type ChartPayloadItem = {
	dataKey?: string;
	value?: number;
	payload?: { tooltipLabel?: string };
};

function ChartTooltip({
	active,
	payload,
	label,
}: {
	active?: boolean;
	payload?: ChartPayloadItem[];
	label?: string;
}) {
	if (!active || !payload?.length) return null;
	const exported = Number(
		payload.find((p) => p.dataKey === "exported")?.value ?? 0,
	);
	const tracked = Number(
		payload.find((p) => p.dataKey === "tracked")?.value ?? 0,
	);
	return (
		<div className="rounded-lg border bg-background dark:bg-card p-3 shadow-md text-sm">
			<p className="font-medium text-foreground mb-1">
				{payload[0]?.payload?.tooltipLabel ?? label}
			</p>
			<div className="flex items-center gap-2">
				<span
					className="h-2 w-2 rounded-full"
					style={{ backgroundColor: COLORS.exported }}
				/>
				<span className="text-muted-foreground">Exported:</span>
				<span className="font-medium">{exported.toFixed(1)}h</span>
			</div>
			<div className="flex items-center gap-2">
				<span
					className="h-2 w-2 rounded-full"
					style={{ backgroundColor: COLORS.tracked }}
				/>
				<span className="text-muted-foreground">To Export:</span>
				<span className="font-medium">{tracked.toFixed(1)}h</span>
			</div>
			<div className="border-t mt-1 pt-1 flex items-center gap-2">
				<span className="text-muted-foreground">Total:</span>
				<span className="font-semibold">
					{(exported + tracked).toFixed(1)}h
				</span>
			</div>
		</div>
	);
}

// ─────────────────────────────────────────────
// Stacked Bar Shape (rounds top corners of topmost visible segment)
// ─────────────────────────────────────────────

const STACK_RADIUS = 4;

type BarShapeProps = {
	x?: number;
	y?: number;
	width?: number;
	height?: number;
	fill?: string;
	index?: number;
};

function RoundedTopBar(props: BarShapeProps) {
	const { x = 0, y = 0, width = 0, height = 0, fill } = props;
	if (!height || height <= 0) return null;
	const r = Math.min(STACK_RADIUS, height / 2, width / 2);
	return (
		<path
			d={`M${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} L${x},${y + height} Z`}
			fill={fill ?? "currentColor"}
		/>
	);
}

function FlatBar(props: BarShapeProps) {
	const { x = 0, y = 0, width = 0, height = 0, fill } = props;
	if (!height || height <= 0) return null;
	return (
		<rect
			x={x}
			y={y}
			width={width}
			height={height}
			fill={fill ?? "currentColor"}
		/>
	);
}

// ─────────────────────────────────────────────
// Sortable Table Header
// ─────────────────────────────────────────────

function SortableHeader({
	label,
	sortKey,
	currentSort,
	currentDir,
	onSort,
}: {
	label: string;
	sortKey: string;
	currentSort: string;
	currentDir: "asc" | "desc";
	onSort: (key: string) => void;
}) {
	const active = currentSort === sortKey;
	return (
		<th
			className="px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
			onClick={() => onSort(sortKey)}
		>
			{label}
			{active && (
				<span className="ml-1">{currentDir === "asc" ? "↑" : "↓"}</span>
			)}
		</th>
	);
}

const REPORTING_BASE_FILTERS: EntryFilterState = { status: ["exported"] };
const REPORTING_STATUS_VALUES = ["tracked", "reviewed", "exported"] as const;

function timekeeperValues(
	value: EntryFilterState["timekeeper"],
): string[] | undefined {
	if (value === undefined) return undefined;
	return value;
}

function sameValues(a: readonly string[], b: readonly string[]): boolean {
	if (a.length !== b.length) return false;
	const set = new Set(a);
	return b.every((value) => set.has(value));
}

function coerceReportingFilters(
	raw: Partial<EntryFilterState>,
	defaultFilters: EntryFilterState,
): EntryFilterState {
	const allowedStatuses = new Set<string>(REPORTING_STATUS_VALUES);
	const rawStatus = raw.status;
	const status =
		rawStatus === undefined
			? [...defaultFilters.status]
			: rawStatus.filter((value) => allowedStatuses.has(value));
	const next: EntryFilterState = { status };
	const rawTimekeepers = timekeeperValues(raw.timekeeper);
	const defaultTimekeepers = timekeeperValues(defaultFilters.timekeeper);
	if (rawTimekeepers !== undefined) {
		next.timekeeper = rawTimekeepers;
	} else if (defaultTimekeepers !== undefined) {
		next.timekeeper = defaultTimekeepers;
	}
	for (const field of [
		"application",
		"matter",
		"client",
		"billableCategory",
	] as const) {
		if (raw[field] !== undefined) next[field] = raw[field];
	}
	return next;
}

function readReportingFilters(
	searchParams: URLSearchParams,
	defaultFilters: EntryFilterState,
	selfProfId: string | null,
	teamProfIds: readonly string[],
): EntryFilterState {
	const rawFilter = searchParams.get("filter");
	if (rawFilter != null) {
		return coerceReportingFilters(
			filterStateParser.parse(rawFilter) ?? {},
			defaultFilters,
		);
	}

	const legacyStatus = searchParams.get("status");
	const raw: EntryFilterState = {
		status: [...defaultFilters.status],
	};
	const defaultTimekeepers = timekeeperValues(defaultFilters.timekeeper);
	if (defaultTimekeepers !== undefined) raw.timekeeper = defaultTimekeepers;

	const legacyScope = searchParams.get("attorney");
	if (legacyScope === "me") {
		if (selfProfId) raw.timekeeper = [selfProfId];
	} else if (legacyScope === "team") {
		raw.timekeeper = [...teamProfIds];
	} else if (legacyScope) {
		raw.timekeeper = [legacyScope];
	}

	if (legacyStatus === "tracked") raw.status = ["tracked", "reviewed"];
	if (legacyStatus === "all") raw.status = ["tracked", "reviewed", "exported"];
	if (legacyStatus === "exported") raw.status = ["exported"];

	const legacyClient = searchParams.get("client");
	if (legacyClient) raw.client = [legacyClient];

	const legacyMatter = searchParams.get("matter");
	if (legacyMatter) raw.matter = [legacyMatter];

	return coerceReportingFilters(raw, defaultFilters);
}

function fieldOptionValues(
	fieldOptions: FieldOptions,
	field: EnumFilterField,
): string[] {
	return normalizeOptions(fieldOptions[field] ?? []).map(
		(option) => option.value,
	);
}

const ENTRIES_TABLE_PREF_PARAMS = ["sort", "cols", "colOrder"] as const;
const ENTRIES_STALE_PARAMS = [
	"attorney",
	"status",
	"client",
	"matter",
	"date",
	"month",
	"q",
] as const;

async function fetchEntriesForReportingExcel(
	makeApiRequest: ReturnType<typeof useApi>["makeApiRequest"],
	filters: EntryFilterState,
	dateRange: { from: Date; to: Date },
): Promise<Entry[]> {
	return fetchAllEntries(
		{ makeApiRequest },
		entryFiltersToEntriesScope(
			filters,
			{ from: toDateKey(dateRange.from), to: toDateKey(dateRange.to) },
			{ includeAttachments: false },
		),
	);
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export default function ReportingDashboardView() {
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { makeApiRequest } = useApi();
	const { reports, isLoading: reportsLoading } = useReportsCache();

	// URL param helpers
	const setParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					for (const [key, value] of Object.entries(updates)) {
						if (!value) next.delete(key);
						else next.set(key, value);
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	// Leave the timekeeper filter undefined by default so the backend owns the
	// default scope. Explicit selections are represented as arrays.
	const selfProfId = reports.self?.prof_id ?? null;
	const teamProfIds = useMemo(
		() => reports.manages.map((person) => person.prof_id),
		[reports.manages],
	);
	const defaultReportingFilters = useMemo<EntryFilterState>(
		() => ({ ...REPORTING_BASE_FILTERS }),
		[],
	);
	const reportingFilters = useMemo(
		() =>
			readReportingFilters(
				searchParams,
				defaultReportingFilters,
				selfProfId,
				teamProfIds,
			),
		[searchParams, defaultReportingFilters, selfProfId, teamProfIds],
	);
	const selectedTimekeeperIds = useMemo(
		() => timekeeperValues(reportingFilters.timekeeper) ?? [],
		[reportingFilters.timekeeper],
	);

	// Date range from URL (from= and to=), defaulting to last 30 days
	const dateRange = useMemo(() => {
		const fromStr = searchParams.get("from");
		const toStr = searchParams.get("to");
		if (fromStr && toStr) {
			const [fy, fm, fd] = fromStr.split("-").map(Number);
			const [ty, tm, td] = toStr.split("-").map(Number);
			return { from: new Date(fy, fm - 1, fd), to: new Date(ty, tm - 1, td) };
		}
		return getDateRange("last-30");
	}, [searchParams]);

	// Detect which preset matches the current range (for highlighting)
	const activePreset = useMemo((): DatePreset | null => {
		const fromKey = toDateKey(dateRange.from);
		const toKey = toDateKey(dateRange.to);
		for (const key of Object.keys(DATE_PRESET_LABELS) as DatePreset[]) {
			const r = getDateRange(key);
			if (toDateKey(r.from) === fromKey && toDateKey(r.to) === toKey)
				return key;
		}
		return null;
	}, [dateRange]);

	const setDateRange = useCallback(
		(from: Date, to: Date) => {
			setParams({ from: toDateKey(from), to: toDateKey(to) });
		},
		[setParams],
	);

	// Table sort
	const [tableSort, setTableSort] = useState<string>("total");
	const [tableSortDir, setTableSortDir] = useState<"asc" | "desc">("desc");
	const [isExportingExcel, setIsExportingExcel] = useState(false);

	const handleSort = useCallback(
		(key: string) => {
			if (tableSort === key) {
				setTableSortDir((d) => (d === "asc" ? "desc" : "asc"));
			} else {
				setTableSort(key);
				setTableSortDir("desc");
			}
		},
		[tableSort],
	);

	// Dashboard data
	const dashboard = useReportingDashboard({
		dateRange,
		scope: "all",
		filters: reportingFilters,
		enabled: !reportsLoading,
	});

	const {
		stats,
		dailyChart,
		memberTable,
		topClients,
		topMatters,
		fieldOptions: dashboardFieldOptions,
	} = dashboard;

	const timekeeperOptions = useMemo(() => {
		const options = [];
		if (reports.self) {
			options.push({
				value: reports.self.prof_id,
				label: reports.self.full_name,
				sublabel: reports.self.email,
			});
		}
		for (const person of reports.manages) {
			options.push({
				value: person.prof_id,
				label: person.full_name,
				sublabel: person.email,
			});
		}
		return options;
	}, [reports.self, reports.manages]);
	const fieldOptions = useMemo<FieldOptions>(
		() => ({
			...dashboardFieldOptions,
			timekeeper: timekeeperOptions,
		}),
		[dashboardFieldOptions, timekeeperOptions],
	);

	const navigateToEntriesDrilldown = useCallback(
		(
			patch: Partial<EntryFilterState> = {},
			range: { from: Date; to: Date } = dateRange,
		) => {
			const next = new URLSearchParams();

			// Reporting defaults to a multi-day range while Entries defaults to today,
			// so every drilldown must pin the visible reporting range explicitly.
			next.set("from", toDateKey(range.from));
			next.set("to", toDateKey(range.to));

			for (const key of ENTRIES_TABLE_PREF_PARAMS) {
				const value = searchParams.get(key);
				if (value) next.set(key, value);
			}

			const filters: EntryFilterState = { ...reportingFilters, ...patch };
			// If reporting is showing an implicit manager/team aggregate, materialize
			// that scope in the Entries URL so the entries scoped cache fetches all
			// represented timekeepers rather than falling back to the logged-in user.
			if (filters.timekeeper === undefined) {
				const allDisplayedTimekeepers = timekeeperOptions.map(
					(option) => option.value,
				);
				if (allDisplayedTimekeepers.length > 0) {
					filters.timekeeper = allDisplayedTimekeepers;
				}
			}

			next.set("filter", serializeEntryFilters(filters));

			// Intentionally do not carry legacy/free-text params that could hide rows
			// and make the drilldown look empty or stale.
			for (const key of ENTRIES_STALE_PARAMS) next.delete(key);

			navigate(`/entries?${next.toString()}`);
		},
		[dateRange, navigate, reportingFilters, searchParams, timekeeperOptions],
	);

	const updateFilters = useCallback(
		(nextFilters: EntryFilterState) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					const normalized = coerceReportingFilters(
						nextFilters,
						defaultReportingFilters,
					);
					if (isDefaultFilters(normalized, defaultReportingFilters)) {
						next.delete("filter");
					} else {
						next.set("filter", serializeEntryFilters(normalized));
					}
					// Drop legacy reporting filter params once the shared filter menu is used.
					next.delete("attorney");
					next.delete("status");
					next.delete("client");
					next.delete("matter");
					return next;
				},
				{ replace: true },
			);
		},
		[defaultReportingFilters, setSearchParams],
	);

	const setFilterFieldValues = useCallback(
		(field: NonStatusMultiSelectFilterField, values: readonly string[]) => {
			updateFilters({ ...reportingFilters, [field]: [...values] });
		},
		[reportingFilters, updateFilters],
	);
	const setFilterStatus = useCallback(
		(values: readonly string[]) =>
			updateFilters({ ...reportingFilters, status: [...values] }),
		[reportingFilters, updateFilters],
	);
	const setFilterTimekeepers = useCallback(
		(values: readonly string[]) =>
			updateFilters({ ...reportingFilters, timekeeper: [...values] }),
		[reportingFilters, updateFilters],
	);
	const toggleFilterFieldValue = useCallback(
		(field: EnumFilterField, value: string) => {
			if (field === "timekeeper") {
				const explicit = timekeeperValues(reportingFilters.timekeeper);
				const base = explicit ?? fieldOptionValues(fieldOptions, "timekeeper");
				const has = base.includes(value);
				setFilterTimekeepers(
					has ? base.filter((v) => v !== value) : [...base, value],
				);
				return;
			}
			if (field === "status") {
				const has = reportingFilters.status.includes(value);
				setFilterStatus(
					has
						? reportingFilters.status.filter((v) => v !== value)
						: [...reportingFilters.status, value],
				);
				return;
			}
			const explicit = reportingFilters[field];
			const base = explicit ?? fieldOptionValues(fieldOptions, field);
			const has = base.includes(value);
			setFilterFieldValues(
				field,
				has ? base.filter((v) => v !== value) : [...base, value],
			);
		},
		[
			reportingFilters,
			fieldOptions,
			setFilterFieldValues,
			setFilterStatus,
			setFilterTimekeepers,
		],
	);
	const selectAllFilterValues = useCallback(
		(field: EnumFilterField) => {
			if (field === "timekeeper") {
				setFilterTimekeepers(fieldOptionValues(fieldOptions, "timekeeper"));
				return;
			}
			if (field === "status") {
				setFilterStatus(fieldOptionValues(fieldOptions, "status"));
				return;
			}
			const next = { ...reportingFilters };
			delete next[field];
			updateFilters(next);
		},
		[
			reportingFilters,
			fieldOptions,
			setFilterStatus,
			setFilterTimekeepers,
			updateFilters,
		],
	);
	const selectNoFilterValues = useCallback(
		(field: EnumFilterField) => {
			if (field === "timekeeper") setFilterTimekeepers([]);
			if (field === "status") setFilterStatus([]);
			else if (field !== "timekeeper") setFilterFieldValues(field, []);
		},
		[setFilterFieldValues, setFilterStatus, setFilterTimekeepers],
	);
	const resetFilters = useCallback(
		() => updateFilters(defaultReportingFilters),
		[defaultReportingFilters, updateFilters],
	);
	const setOnlyTimekeepers = useCallback(
		(values: readonly string[]) =>
			updateFilters({ ...reportingFilters, timekeeper: [...values] }),
		[reportingFilters, updateFilters],
	);
	const filterShortcuts = useMemo<
		Partial<Record<EnumFilterField, FieldShortcut[]>>
	>(
		() => ({
			timekeeper: [
				...(selfProfId && reports.self
					? [
							{
								label: "Me",
								description: reports.self.full_name,
								active: sameValues(selectedTimekeeperIds, [selfProfId]),
								onSelect: () => setOnlyTimekeepers([selfProfId]),
							},
						]
					: []),
				...(teamProfIds.length > 0
					? [
							{
								label: "My Team",
								description: `${teamProfIds.length} timekeeper${teamProfIds.length === 1 ? "" : "s"}`,
								active: sameValues(selectedTimekeeperIds, teamProfIds),
								onSelect: () => setOnlyTimekeepers(teamProfIds),
							},
						]
					: []),
			],
		}),
		[
			selfProfId,
			reports.self,
			selectedTimekeeperIds,
			setOnlyTimekeepers,
			teamProfIds,
		],
	);

	const filterMenuSharedProps = {
		fieldOptions,
		filters: reportingFilters,
		defaultFilters: defaultReportingFilters,
		fieldShortcuts: filterShortcuts,
		singleSelectFields: { timekeeper: false },
		onToggleFieldValue: toggleFilterFieldValue,
		onSelectAll: selectAllFilterValues,
		onSelectNone: selectNoFilterValues,
		onReset: resetFilters,
	};

	const hasActiveFilters = !isDefaultFilters(
		reportingFilters,
		defaultReportingFilters,
	);

	// Sort member table
	const sortedMembers = useMemo(() => {
		const mult = tableSortDir === "asc" ? 1 : -1;
		return [...memberTable].sort((a, b) => {
			const av = a[tableSort as keyof MemberRow] ?? 0;
			const bv = b[tableSort as keyof MemberRow] ?? 0;
			if (typeof av === "string" && typeof bv === "string")
				return mult * av.localeCompare(bv);
			return mult * ((av as number) - (bv as number));
		});
	}, [memberTable, tableSort, tableSortDir]);

	const memberTotals = useMemo(
		() => ({
			tracked: memberTable.reduce((s, m) => s + m.tracked, 0),
			exported: memberTable.reduce((s, m) => s + m.exported, 0),
			total: memberTable.reduce((s, m) => s + m.total, 0),
			entries: memberTable.reduce((s, m) => s + m.entries, 0),
		}),
		[memberTable],
	);

	const handleExportExcel = useCallback(async () => {
		if (isExportingExcel) return;
		setIsExportingExcel(true);
		try {
			const exportFilters: EntryFilterState = { ...reportingFilters };
			if (exportFilters.timekeeper === undefined) {
				const allDisplayedTimekeepers = timekeeperOptions.map(
					(option) => option.value,
				);
				if (allDisplayedTimekeepers.length > 0) {
					exportFilters.timekeeper = allDisplayedTimekeepers;
				}
			}
			const fetchedEntries = await fetchEntriesForReportingExcel(
				makeApiRequest,
				exportFilters,
				dateRange,
			);
			const predicate = makeEntryPredicate(exportFilters, {
				from: toDateKey(dateRange.from),
				to: toDateKey(dateRange.to),
			});
			const entries = fetchedEntries.filter(predicate);

			await exportReportingDashboardToExcel({
				firmName: getFirmDisplayName(),
				dateRange,
				filters: exportFilters,
				fieldOptions,
				stats,
				dailyChart,
				topClients,
				topMatters,
				memberTable: sortedMembers,
				entries,
			});
			toast.success("Excel exported");
		} catch (error) {
			logger.error("Failed to export reporting Excel", {
				error: error instanceof Error ? error.message : String(error),
			});
			toast.error("Failed to export Excel");
		} finally {
			setIsExportingExcel(false);
		}
	}, [
		isExportingExcel,
		makeApiRequest,
		dateRange,
		reportingFilters,
		timekeeperOptions,
		fieldOptions,
		stats,
		dailyChart,
		topClients,
		topMatters,
		sortedMembers,
	]);

	const navigateToCurrentEntries = useCallback(
		() => navigateToEntriesDrilldown(),
		[navigateToEntriesDrilldown],
	);
	const navigateToExportedEntries = useCallback(
		() => navigateToEntriesDrilldown({ status: ["exported"] }),
		[navigateToEntriesDrilldown],
	);
	const navigateToExportableEntries = useCallback(
		() => navigateToEntriesDrilldown({ status: ["tracked", "reviewed"] }),
		[navigateToEntriesDrilldown],
	);
	const navigateToChartDay = useCallback(
		(point: unknown, status: string[]) => {
			const payload = point as { payload?: { date?: string } };
			const date = payload.payload?.date;
			if (!date) return;
			const [year, month, day] = date.split("-").map(Number);
			const clickedDate = new Date(year, month - 1, day);
			navigateToEntriesDrilldown(
				{ status },
				{ from: clickedDate, to: clickedDate },
			);
		},
		[navigateToEntriesDrilldown],
	);

	if (reportsLoading || dashboard.isLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Loading dashboard...</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full bg-background overflow-y-auto">
			{/* Filter bar */}
			<div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 px-6 py-4 bg-background border-b border-border">
				<FilterMenu
					{...filterMenuSharedProps}
					trigger={
						<Button
							variant="outline"
							size="icon"
							aria-label="Filter"
							className={cn(
								"relative",
								hasActiveFilters &&
									"border-ring text-ring hover:border-ring hover:text-ring",
							)}
						>
							<Filter />
							{hasActiveFilters && (
								<span className="absolute right-1 top-1 size-1.5 rounded-full bg-ring" />
							)}
						</Button>
					}
				/>

				{/* Date range */}
				<DatePicker
					className="w-fit"
					dateRange={dateRange}
					onDateChange={(day) => setDateRange(day, day)}
					onDateRangeChange={(range) => {
						setDateRange(range.from, range.to);
					}}
					selectionMode="range"
					sideSlot={({ selectRange }) => (
						<div className="flex flex-col border-r p-1 w-[160px]">
							{Object.entries(DATE_PRESET_LABELS).map(([key, presetLabel]) => {
								const range = getDateRange(key as DatePreset);

								return (
									<button
										key={key}
										type="button"
										className={cn(
											"flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-muted text-left",
											activePreset === key && "bg-muted font-medium",
										)}
										onClick={() => selectRange(range)}
									>
										<Check
											className={cn(
												"h-3.5 w-3.5 shrink-0",
												activePreset === key ? "opacity-100" : "opacity-0",
											)}
										/>
										{presetLabel}
									</button>
								);
							})}
						</div>
					)}
				/>

				<TooltipButton
					variant="outline"
					size="icon"
					wrapperClassName="ml-auto"
					aria-label="Export Excel"
					tooltip="Export Excel"
					loading={isExportingExcel}
					disabled={dashboard.isFetching || Boolean(dashboard.error)}
					onClick={handleExportExcel}
				>
					<Download />
				</TooltipButton>
			</div>

			{/* Content */}
			<div className="flex-1 p-6 space-y-6">
				{dashboard.error && (
					<div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
						{dashboard.error}
					</div>
				)}

				{/* Stat cards */}
				<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
					<StatCard
						label="Total Hours"
						value={stats.totalHours.toFixed(1)}
						unit="hrs"
						subtitle={`${stats.entryCount} entries`}
						gradient="from-[var(--brand-dark-teal)] to-[var(--brand-dark-green)]"
						onClick={navigateToCurrentEntries}
						ariaLabel="View entries for this reporting range"
					/>
					<StatCard
						label="Exported"
						value={stats.exportedHours.toFixed(1)}
						unit="hrs"
						gradient="from-[var(--brand-600)] to-[var(--brand-900)]"
						onClick={navigateToExportedEntries}
						ariaLabel="View exported entries for this reporting range"
					/>
					<StatCard
						label="To Export"
						value={stats.trackedHours.toFixed(1)}
						unit="hrs"
						gradient="from-[var(--brand-500)] to-[var(--brand-700)]"
						onClick={navigateToExportableEntries}
						ariaLabel="View tracked and reviewed entries for this reporting range"
					/>
					<StatCard
						label="Matters"
						value={String(stats.matterCount)}
						gradient="from-[var(--brand-900)] to-[var(--brand-950)]"
						onClick={navigateToCurrentEntries}
						ariaLabel="View entries for matters in this reporting range"
					/>
					<StatCard
						label="Avg / Day"
						value={stats.avgPerDay.toFixed(1)}
						unit="hrs"
						subtitle="weekdays"
						gradient="from-[var(--brand-dark-slate)] to-[var(--brand-mid-slate)]"
						onClick={navigateToCurrentEntries}
						ariaLabel="View entries for average daily hours in this reporting range"
					/>
				</div>

				{/* Daily chart */}
				<div className="rounded-lg border bg-background dark:bg-card p-4 shadow-xs">
					<h3 className="text-sm font-medium text-foreground mb-4">
						Hours by Day
					</h3>

					<ResponsiveContainer width="100%" height={300}>
						<BarChart
							data={dailyChart}
							margin={{ top: 5, right: 5, bottom: 20, left: 0 }}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke={CHART_COLORS.grid}
								vertical={false}
							/>
							<XAxis
								dataKey="dateLabel"
								tick={{ fontSize: 11, fill: CHART_COLORS.axis }}
								tickLine={false}
								axisLine={false}
								angle={-35}
								textAnchor="end"
								interval={dailyChart.length > 40 ? 6 : 0}
							/>
							<YAxis
								tick={{ fontSize: 11, fill: CHART_COLORS.axis }}
								tickLine={false}
								axisLine={false}
								width={35}
							/>
							<Tooltip
								content={<ChartTooltip />}
								cursor={{ fill: CHART_COLORS.cursor }}
							/>
							<Legend
								iconType="circle"
								iconSize={8}
								wrapperStyle={{ fontSize: 12 }}
							/>
							<Bar
								dataKey="exported"
								stackId="a"
								fill={COLORS.exported}
								name="Exported"
								cursor="pointer"
								onClick={(point) => navigateToChartDay(point, ["exported"])}
								shape={(props: unknown) => {
									const shapeProps = props as BarShapeProps;
									const { index = 0 } = shapeProps;
									const entry = dailyChart[index];
									return entry && !entry.tracked ? (
										<RoundedTopBar {...shapeProps} />
									) : (
										<FlatBar {...shapeProps} />
									);
								}}
							/>
							<Bar
								dataKey="tracked"
								stackId="a"
								fill={COLORS.tracked}
								name="To Export"
								cursor="pointer"
								onClick={(point) =>
									navigateToChartDay(point, ["tracked", "reviewed"])
								}
								shape={RoundedTopBar}
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>

				{/* Clients (left) + Matters (right) */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{/* Clients */}
					<div className="rounded-lg border bg-background dark:bg-card shadow-xs overflow-hidden">
						<div className="px-4 py-3 border-b">
							<h3 className="text-sm font-medium text-foreground">Clients</h3>
						</div>
						<div className="overflow-y-auto min-h-[120px] max-h-[360px] p-4">
							{topClients.length > 0 ? (
								<div className="space-y-2">
									{topClients.map((c) => (
										<button
											type="button"
											key={c.client}
											className="flex items-center gap-3 w-full text-left rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors cursor-pointer"
											onClick={() => {
												navigateToEntriesDrilldown({
													client: [c.client],
													matter: undefined,
												});
											}}
											aria-label={`View entries for client ${c.client}`}
										>
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium truncate">
													{c.client}
												</div>
												<div className="text-xs text-muted-foreground">
													{c.entries} entries
												</div>
											</div>
											<div className="text-sm font-medium tabular-nums w-16 text-right">
												{c.hours.toFixed(1)}h
											</div>
											<div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
												<div
													className="h-full rounded-full"
													style={{
														width: `${Math.min(100, (c.hours / (topClients[0]?.hours || 1)) * 100)}%`,
														backgroundColor: COLORS.exported,
													}}
												/>
											</div>
										</button>
									))}
								</div>
							) : (
								<p className="text-sm text-muted-foreground text-center py-8">
									No client data for this period
								</p>
							)}
						</div>
					</div>

					{/* Matters */}
					<div className="rounded-lg border bg-background dark:bg-card shadow-xs overflow-hidden">
						<div className="px-4 py-3 border-b">
							<h3 className="text-sm font-medium text-foreground">Matters</h3>
						</div>
						<div className="overflow-y-auto min-h-[120px] max-h-[360px] p-4">
							{topMatters.length > 0 ? (
								<div className="space-y-2">
									{topMatters.map((m) => (
										<button
											type="button"
											key={m.matterId}
											className="flex items-center gap-3 w-full text-left rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors cursor-pointer"
											onClick={() =>
												navigateToEntriesDrilldown({
													matter: [m.matterNumber || m.matterId],
													client: [m.client],
												})
											}
											aria-label={`View entries for matter ${m.matterNumber || m.matterId}`}
										>
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium truncate">
													{m.matterNumber || m.matterId}
												</div>
												<div className="text-xs text-muted-foreground truncate">
													{m.matterDescription} · {m.client}
												</div>
											</div>
											<div className="text-sm font-medium tabular-nums w-16 text-right">
												{m.hours.toFixed(1)}h
											</div>
											<div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
												<div
													className="h-full rounded-full"
													style={{
														width: `${Math.min(100, (m.hours / (topMatters[0]?.hours || 1)) * 100)}%`,
														backgroundColor: COLORS.exported,
													}}
												/>
											</div>
										</button>
									))}
								</div>
							) : (
								<p className="text-sm text-muted-foreground text-center py-8">
									No matter data for this period
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Timekeepers — full width */}
				{selectedTimekeeperIds.length !== 1 && (
					<div className="rounded-lg border bg-background dark:bg-card shadow-xs">
						<div className="px-4 py-3 border-b">
							<h3 className="text-sm font-medium text-foreground">
								Timekeepers
							</h3>
						</div>
						<div>
							<table className="w-full text-sm">
								<thead className="sticky top-[65px] z-[5]">
									<tr className="border-b bg-muted shadow-[0_1px_0_0_var(--border)]">
										<SortableHeader
											label="Name"
											sortKey="profName"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="To Export"
											sortKey="tracked"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Exported"
											sortKey="exported"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Total"
											sortKey="total"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Entries"
											sortKey="entries"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Matters"
											sortKey="matters"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Avg/Day"
											sortKey="avgPerDay"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
									</tr>
								</thead>
								<tbody>
									{sortedMembers.map((m) => (
										<tr
											key={m.profId}
											className="border-b hover:bg-muted/20 transition-colors"
										>
											<td className="px-3 py-2">
												<button
													type="button"
													className="block w-full rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
													onClick={() =>
														navigateToEntriesDrilldown({
															timekeeper: [m.profId],
														})
													}
													aria-label={`View entries for timekeeper ${m.profName}`}
												>
													<div className="font-medium text-foreground">
														{m.profName}
													</div>
													{m.profType && (
														<div className="text-xs text-muted-foreground">
															{m.profType}
														</div>
													)}
												</button>
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.tracked.toFixed(1)}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.exported.toFixed(1)}
											</td>
											<td className="px-3 py-2 font-medium">
												{m.total.toFixed(1)}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.entries}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.matters}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.avgPerDay.toFixed(1)}
											</td>
										</tr>
									))}
								</tbody>
								<tfoot className="sticky bottom-0 z-[5]">
									<tr className="bg-muted font-medium border-t shadow-[0_-1px_0_0_var(--border)] [&>td:first-child]:rounded-bl-lg [&>td:last-child]:rounded-br-lg">
										<td className="px-3 py-2">Total</td>
										<td className="px-3 py-2">
											{memberTotals.tracked.toFixed(1)}
										</td>
										<td className="px-3 py-2">
											{memberTotals.exported.toFixed(1)}
										</td>
										<td className="px-3 py-2">
											{memberTotals.total.toFixed(1)}
										</td>
										<td className="px-3 py-2">{memberTotals.entries}</td>
										<td className="px-3 py-2" />
										<td className="px-3 py-2" />
									</tr>
								</tfoot>
							</table>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
